import React, { useContext, useEffect, useState } from 'react';
import { CountryCode } from '@dayetopia/library/types';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Context } from '@components/Context';
import { VMS_TICK_GOLD_IMG } from '@constants';
import { PageSlice, ProductCode } from '@contracts';
import { getProductPrice } from '@utils/price';
import useNavigate from '@utils/useNavigate';
import Button from './Button';
import Colors from './Colors';
import { BodyLG } from './TextTypes';
import useDesktop from './useDesktop';

const defaultPrices = {
  standard: '14.99',
  advanced: '54.99',
  premium: '199.99'
};

interface Props {
  slice: PageSlice;
  country?: CountryCode;
}

const PPCPackages: React.FC<Props> = ({ slice }) => {
  const { t } = useTranslation();
  const { navigateWrapper } = useNavigate();
  const { state } = useContext(Context);
  const { currency, country } = state;
  const [prices, setPrices] = useState(defaultPrices);
  const isDesktop = useDesktop(768);

  useEffect(() => {
    getPackagePrices();
  }, []);

  async function getPackagePrices() {
    const standardPrice = await getProductPrice(ProductCode.PPCStandard, currency, country);
    const advancedPrice = await getProductPrice(ProductCode.PPCAdvanced, currency, country);
    const premiumPrice = await getProductPrice(ProductCode.PPCPremium, currency, country);

    setPrices({
      standard: standardPrice.price.toFixed(2),
      advanced: advancedPrice.price.toFixed(2),
      premium: premiumPrice.price.toFixed(2)
    });
  }

  const handleButtonClick = () => {
    navigateWrapper('/ppc-questionnaire/');
  };

  const renderPackageBox = (text: string, price: string, description: string) => {
    const [mainPrice, decimal] = price.split('.');
    return (
      <BoxContainer>
        {isDesktop ? (
          <>
            <Subtitle $fontSize="13px" $mobileFontSize="12px">
              {text}
            </Subtitle>
            <RowWrap>
              <PriceText>{currency.symbol}</PriceText>
              <PriceText>{mainPrice}</PriceText>
              <PriceText style={{ fontSize: '14px' }}>{decimal}</PriceText>
            </RowWrap>
            <DescriptionText>{description}</DescriptionText>
          </>
        ) : (
          <ContentWrap>
            <TextWrap>
              <Subtitle $fontSize="13px" $mobileFontSize="12px">
                {text}
              </Subtitle>
              <DescriptionText>{description}</DescriptionText>
            </TextWrap>
            <RowWrap>
              <PriceText>{currency.symbol}</PriceText>
              <PriceText>{mainPrice}</PriceText>
              <PriceText style={{ fontSize: '12px' }}>{decimal}</PriceText>
            </RowWrap>
          </ContentWrap>
        )}
      </BoxContainer>
    );
  };

  return (
    <Container>
      <Inner>
        <Title>{t('ppc-packages.title')}</Title>
        <Subtitle>{t('ppc-packages.subtitle')}</Subtitle>
        <PackagesWrap>
          {renderPackageBox(t('ppc-packages.standard'), prices.standard, t('ppc-packages.standard-description'))}
          {country === CountryCode.US
            ? renderPackageBox(t('ppc-packages.advanced'), prices.advanced, t('ppc-packages.advanced-description-us'))
            : renderPackageBox(t('ppc-packages.advanced'), prices.advanced, t('ppc-packages.advanced-description'))}
          {/* {renderPackageBox(t('ppc-packages.premium'), prices.premium, t('ppc-packages.premium-description'))} */}
        </PackagesWrap>
        <RowWrap style={{ alignItems: 'center' }}>
          <img src={VMS_TICK_GOLD_IMG} height="16px" width="16px" />
          <span>{t('ppc-packages.one-time-purchase')}</span>
        </RowWrap>
        <Button style={{ marginTop: '40px' }} onClick={() => handleButtonClick()}>
          {t('ppc-packages.start-for-free')}
        </Button>
      </Inner>
    </Container>
  );
};

export default PPCPackages;

interface StyleProps {
  $fontSize?: string;
  $mobileFontSize?: string;
}

const Container = styled.div`
  width: 100%;
  display: flex;
  padding: 30px;
  color: ${Colors.navyBlue};

  @media (max-width: 1024px) {
    padding: 30px;

    &.align-carousel {
      padding: 0 0 60px 0;
    }
  }
`;

const Inner = styled.div`
  display: flex;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  align-items: center;
  flex-direction: column;
  justify-content: space-between;

  @media (max-width: 1024px) {
    flex-direction: column;
    padding: 0 20px;
  }
`;

const Title = styled.div`
  font-family: Domaine;
  font-style: normal;
  font-weight: 600;
  font-size: 30px;
  line-height: 120%;
  display: flex;
  text-align: center;
  align-items: flex-end;
  margin-bottom: 40px;

  @media (max-width: 1024px) {
    font-size: 24px;
  }
`;

const Subtitle = styled.div<StyleProps>`
  font-family: BrownStd;
  font-style: normal;
  font-weight: 700;
  font-size: ${(props) => (props.$fontSize ? props.$fontSize : '15px')};
  line-height: 140%;
  letter-spacing: 1.5px;
  text-transform: uppercase;

  @media (max-width: 1024px) {
    font-size: ${(props) => (props.$mobileFontSize ? props.$mobileFontSize : '14px')};
  }
`;

const BoxContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 30px;
  gap: 20px;
  width: 346.67px;
  height: 205px;
  border: 1px solid ${Colors.ecru};
  border-radius: 20px;

  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 1;

  @media (max-width: 1024px) {
    height: auto;
  }
`;

const PriceText = styled.div`
  font-family: Domaine;
  font-style: normal;
  font-weight: 600;
  font-size: 30px;
  line-height: 110%;
  display: flex;
  text-align: center;
  letter-spacing: -0.01em;
  height: 100%;

  @media (max-width: 1024px) {
    font-size: 20px;
  }
`;

const DescriptionText = styled(BodyLG)`
  max-width: 250px;

  @media (max-width: 1024px) {
    font-size: 13px;
    text-align: left;
  }
`;

const PackagesWrap = styled.div`
  display: flex;
  flex-direction: row;
  padding: 30px 0;
  gap: 40px;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const RowWrap = styled.div`
  display: flex;
  gap: 3px;
  align-items: flex-start;

  span {
    font-family: BrownStd;
    font-weight: 700;
    font-size: 14px;
    line-height: 150%;
  }
`;

const ContentWrap = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

const TextWrap = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 144px;
  gap: 5px;
`;
